import moment from "moment"
import queryString from "query-string"
import { create } from "zustand"
import { devtools, persist } from "zustand/middleware"
import { shallow } from "zustand/shallow"
import { createWithEqualityFn } from "zustand/traditional"

import {
  StoreApiState,
  CredentialsStoreState,
  BranchesStoreState,
  DateStoreState,
  LicenseStoreState,
} from "./store.interfaces"

// Function to determine WebSocket (WS) protocol based on the provided API protocol
/**
 * @param apiProtocol - The API protocol to determine the WS protocol for.
 * @returns The WS protocol determined based on the provided API protocol.
 */
export const getWsProtocol = (apiProtocol: string): string => {
  let wsProtocol: string | undefined
  if (apiProtocol === "http:") {
    wsProtocol = "ws:"
  } else if (apiProtocol === "https:") {
    wsProtocol = "wss:"
  } else {
    throw Error("Invalid apiProtocol Set")
  }
  return wsProtocol
}

// Define and export the store instance
/**
 * This module creates a Zustand store named `storeApi` to manage API configuration settings.
 * It exports a store instance that includes initial state values for WebSocket protocol, API protocol, hostname, and port.
 * These values are retrieved from the `window.__RUNTIME_CONFIG__` object during runtime.
 * Additionally, a method `setApiSettings` is provided (commented out) to update API settings dynamically if needed.
 */

/**
 * Creates a Zustand store instance for managing API configuration settings.
 *
 * @returns A Zustand store instance with initial state values for WebSocket protocol, API protocol, hostname, port, and client name.
 * These values are retrieved from the `window.__RUNTIME_CONFIG__` object during runtime.
 */
export const storeApi = create<StoreApiState>((set) => ({
  /**
   * WebSocket protocol determined based on the API protocol.
   */
  apiWsProtocol: getWsProtocol(`${window.__RUNTIME_CONFIG__.API_PROTOCOL}:`),
  /**
   * API protocol retrieved from runtime configuration.
   */
  apiProtocol: `${window.__RUNTIME_CONFIG__.API_PROTOCOL}`,
  /**
   * API hostname retrieved from runtime configuration.
   */
  apiHostname: window.__RUNTIME_CONFIG__.API_HOSTNAME,
  /**
   * API port retrieved from runtime configuration.
   */
  apiPort: window.__RUNTIME_CONFIG__.API_PORT,
  /**
   * Client name retrieved from runtime configuration.
   */
  clientName: window.__RUNTIME_CONFIG__.CLIENT_NAME,
}))

/**
 * Creates a Zustand store instance for managing user credentials.
 *
 * @returns A Zustand store instance with initial state values for current user, login error, and network error.
 */
export const useCredentialsStore = create<CredentialsStoreState>()((set) => ({
  currentUser: "",
  loginError: false,
  networkError: false,
  setCurrentUser: (user) => set(() => ({ currentUser: user })),
  setLoginError: (loginError) => set(() => ({ loginError })),
  setNetworkError: (networkError) => set(() => ({ networkError })),
}))

/**
 * Creates a Zustand store instance for managing branch selection.
 *
 * @returns A Zustand store instance with initial state values for selected branch.
 */
export const useBranchesStore = createWithEqualityFn<BranchesStoreState>()(
  persist(
    devtools(
      (set) => ({
        selectedBranch: null,
        setSelectedBranch: (branch) => set({ selectedBranch: branch }),
        selectedBranches: [],
        setSelectedBranches: (branches) => set({ selectedBranches: branches }),
      }),
      { name: "BranchesStore" }
    ),
    {
      name: "branches-store",
      getStorage: () => localStorage,
    }
  )
)

/**
 * Parses the query string from the current window location to extract query parameters.
 *
 * @returns An object containing the parsed query parameters.
 */
const queryParams = queryString.parse(window.location.search)

/**
 * Creates a Zustand store instance for managing date settings.
 *
 * @returns A Zustand store instance with initial state values for start date, end date, and single day.
 */
export const useDateStore = create<DateStoreState>((set) => ({
  startDate:
    queryParams.startDate && typeof queryParams.startDate === "string"
      ? moment(queryParams.startDate)
      : moment().subtract(7, "days"),
  endDate: queryParams.endDate && typeof queryParams.endDate === "string" ? moment(queryParams.endDate) : moment(),
  day: queryParams.day && typeof queryParams.day === "string" ? moment(queryParams.day) : moment(),
  setStartDate: (date) => set(() => ({ startDate: date })),
  setEndDate: (date) => set(() => ({ endDate: date })),
  setSingleDay: (date) => set(() => ({ day: date })),
}))

/**
 * Creates a Zustand store instance for managing license data.
 * Persists license information in localStorage for persistence across sessions.
 *
 * @returns A Zustand store instance with initial state values for license expiry and status.
 */
export const useLicenseStore = createWithEqualityFn<LicenseStoreState>()(
  persist(
    devtools(
      (set) => ({
        licenseInfo: null,
        isWarningDisplayed: false,
        setLicenseInfo: (value) => set({ licenseInfo: value }),
        setIsWarningDisplayed: (value) => set({ isWarningDisplayed: value }),
      }),
      { name: "LicenseStore" }
    ),
    {
      name: "license-store",
      getStorage: () => localStorage,
    }
  ),
  shallow
)

/**
 * Resets all user-related stores to their initial state.
 * This function should be called when logging out to ensure a clean state.
 */
export const resetAllStores = () => {
  // Reset credentials store
  useCredentialsStore.getState().setCurrentUser("")
  useCredentialsStore.getState().setLoginError(false)
  useCredentialsStore.getState().setNetworkError(false)

  // Reset branches store
  useBranchesStore.getState().setSelectedBranch(null)
  useBranchesStore.getState().setSelectedBranches([])

  // Reset date store to default values
  useDateStore.getState().setStartDate(moment().subtract(7, "days"))
  useDateStore.getState().setEndDate(moment())
  useDateStore.getState().setSingleDay(moment())

  // Reset license store
  useLicenseStore.setState({ licenseInfo: null, isWarningDisplayed: false })
}
